import React, { useCallback } from 'react'
import AsyncSelect from 'react-select/async'
import { api } from '../services/api'
import { useDebouncedCallback } from '../hooks'
import selectStyles from '../styles/react-select.module.scss'
import { Competitor } from '../types/forms'

// Option type extends Competitor with react-select required fields
interface CompetitorOption extends Competitor {
  value: string
  label: string
}

export type TeamType = 'singles' | 'doubles' | 'mixed'

export interface CompetitorSelectProps {
  value: Competitor | Competitor[] | null
  onChange: (competitor: Competitor | Competitor[] | null) => void
  teamType?: TeamType
  isMulti?: boolean
  isClearable?: boolean
  placeholder?: string
  className?: string
  isDisabled?: boolean
  isModified?: boolean
}

// Map a competition's `tc_type` / `ext_type` to the mode the picker should
// run in. 'mixed' brackets host either kind of competitor in practice (some
// matches are doubles, some singles), so the picker shows both lists.
// Returns null for unknown / missing types so the caller can fall back to
// another signal.
export function teamTypeFromCompetition(
  competitionType: string | null | undefined,
): TeamType | null {
  if (competitionType === 'singles') return 'singles'
  if (competitionType === 'doubles' || competitionType === 'mixed_doubles') return 'doubles'
  if (competitionType === 'mixed') return 'mixed'
  return null
}

// Convert Competitor to react-select option (exported for use in forms)
export const toOption = (c: Competitor): CompetitorOption => ({ ...c, value: c.tc_id, label: c.name })

const mapPlayer = (item: any): Competitor => ({
  tc_id: item.tc_competitor_id,
  ext_id: item.ext_competitor_id,
  name: item.tc_name || item.ext_name || 'Unknown',
  team_type: item.tc_team_type || item.ext_team_type || 'singles',
})

const mapTeam = (item: any): Competitor => ({
  tc_id: item.tc_team_id,
  ext_id: item.ext_competitor_id,
  name: item.tc_name || item.ext_name || 'Unknown',
  team_type: 'doubles',
})

const CompetitorSelect: React.FC<CompetitorSelectProps> = ({
  value,
  onChange,
  teamType = 'singles',
  isMulti = false,
  isClearable = true,
  placeholder = 'Search competitors...',
  className = '',
  isDisabled = false,
  isModified = false
}) => {
  const fetchCompetitors = useCallback(async (inputValue: string): Promise<CompetitorOption[]> => {
    if (inputValue.length < 2) return []

    try {
      const competitors: Competitor[] = []

      if (teamType === 'singles' || teamType === 'mixed') {
        const playersResp = await api.get('/players', { name: inputValue, limit: 50 })
        competitors.push(...(playersResp?.players || []).map(mapPlayer))
      }

      if (teamType === 'doubles' || teamType === 'mixed') {
        const teamsResp = await api.get('/teams', { name: inputValue, limit: 50 })
        competitors.push(...(teamsResp?.teams || []).map(mapTeam))
      }

      return competitors.map(toOption)
    } catch (error) {
      console.error('Failed to load competitors:', error)
      return []
    }
  }, [teamType])


  const loadCompetitors = useDebouncedCallback(fetchCompetitors, 300)

  // Convert value to option format
  const selectValue = value
    ? (isMulti ? (value as Competitor[]).map(toOption) : toOption(value as Competitor))
    : null

  return (
    <div className={isModified ? selectStyles.modified : ''}>
      <AsyncSelect<CompetitorOption, typeof isMulti>
        value={selectValue}
        onChange={onChange as any}
        loadOptions={loadCompetitors}
        defaultOptions={false}
        openMenuOnFocus={false}
        openMenuOnClick={false}
        isMulti={isMulti}
        isClearable={isClearable}
        placeholder={placeholder}
        className={`${selectStyles.reactSelect} ${className}`}
        classNamePrefix="react-select"
        isDisabled={isDisabled}
        cacheOptions
        getOptionValue={(option) => option.tc_id}
        getOptionLabel={(option) => option.name}
        noOptionsMessage={({ inputValue }) =>
          inputValue.length < 2
            ? "Type at least 2 characters to search..."
            : "No competitors found"
        }
      />
    </div>
  )
}

export default CompetitorSelect
